import type { BlockModel } from '@blocksuite/affine/store';
import { useLiveData, useServiceOptional } from '@toeverything/infra';
import { useEffect, useMemo, useState } from 'react';

import type { DocsService } from '../../doc';
import { EditorService } from '../../editor';

export function useAutomationBlockModel(
  blockId: string | null,
  automationDocId: string | undefined,
  docsService: DocsService
) {
  const editorService = useServiceOptional(EditorService);
  const editor = editorService?.editor;
  const editorContainer = useLiveData(editor?.editorContainer$ ?? null);
  const currentDocId = editorContainer?.doc?.id;

  const [crossDocModel, setCrossDocModel] = useState<BlockModel | null>(null);

  // Current doc lookup (synchronous, no side effects)
  const currentDocModel = useMemo(() => {
    if (!blockId) return null;
    if (editorContainer?.doc) {
      const block = editorContainer.doc.getBlock(blockId);
      if (block?.model) return block.model;
    }
    return null;
  }, [blockId, editorContainer]);

  // E018-S014-T004: Cross-doc lookup (async, has side effects from load())
  useEffect(() => {
    if (
      currentDocModel ||
      !blockId ||
      !automationDocId ||
      automationDocId === currentDocId
    ) {
      setCrossDocModel(null);
      return;
    }

    let releaseRef: (() => void) | undefined;
    try {
      const { doc, release } = docsService.open(automationDocId);
      const blockSuiteDoc = doc.blockSuiteDoc;
      if (blockSuiteDoc) {
        blockSuiteDoc.load();
        const block = blockSuiteDoc.getBlock(blockId);
        if (block?.model) {
          setCrossDocModel(block.model);
          releaseRef = release;
        } else {
          release();
        }
      } else {
        release();
      }
    } catch (e) {
      console.error('[AutomationPanel] Cross-doc block lookup failed:', e);
    }
    return releaseRef ? () => releaseRef() : undefined;
  }, [blockId, automationDocId, currentDocId, currentDocModel, docsService]);

  return currentDocModel ?? crossDocModel;
}
